/**
 * @file DashboardCostAlertBanner.tsx
 * @brief Read-only daily cost alert strip for the super admin dashboard.
 *
 * @details
 * Surfaces today's platform spend vs threshold with a link to Billing
 * for configuration. Only renders when cost alerts are enabled.
 * Draws GridBlock band hairlines so it sits flush in the section grid.
 *
 * @dependencies
 * - features/billing/api (useCostStatus)
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — grid band surface
 */

import { Link } from "react-router-dom";
import { ArrowRight, BellRing, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { useCostStatus } from "@/features/billing/api";
import { cn } from "@/shared/theme/cn";

export function DashboardCostAlertBanner() {
  const { data: status, isLoading } = useCostStatus();

  if (isLoading) {
    return (
      <div className="border-b border-r border-border/60 px-4 py-4 md:px-5">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!status?.enabled) {
    return null;
  }

  const pillVariant: "success" | "warning" | "destructive" = status.is_critical
    ? "destructive"
    : status.is_warning
      ? "warning"
      : "success";

  const Icon = status.is_critical || status.is_warning ? TriangleAlert : BellRing;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 border-b border-r border-border/60 px-4 py-4 md:px-5",
        status.is_critical && "bg-destructive/5",
        status.is_warning && !status.is_critical && "bg-warning/5",
        !status.is_warning && !status.is_critical && "bg-success/5",
      )}
    >
      <Icon
        className={cn(
          "h-5 w-5 shrink-0",
          status.is_critical && "text-destructive",
          status.is_warning && !status.is_critical && "text-warning",
          !status.is_warning && !status.is_critical && "text-success",
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Daily platform cost alert</p>
        <p className="text-sm text-muted-foreground">
          Today:{" "}
          <span className="font-semibold text-foreground">
            ${(status.current_cost / 100).toFixed(2)}
          </span>
          {" / "}
          ${(status.threshold / 100).toFixed(2)} threshold
          {" · "}
          {Math.round(status.percent_used)}% used
        </p>
      </div>
      <Badge variant={pillVariant}>
        {status.is_critical
          ? "Critical"
          : status.is_warning
            ? "Warning"
            : "Normal"}
      </Badge>
      <Link
        to="/admin/billing"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        Configure
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
